import React from "react";
import { motion } from "framer-motion";
import { Github, Linkedin, Mail, ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import ProfileImg from "../../assets/images/profile.jpeg";

const HeroSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <section
      id="home"
      className="relative min-h-[100dvh] md:min-h-screen flex flex-col pt-24 pb-12 md:pt-40 md:pb-20 overflow-hidden bg-zinc-50 dark:bg-black body-font justify-center"
    >
      {/* Dynamic Background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 left-1/4 w-[40rem] h-[40rem] bg-indigo-500/10 rounded-full blur-[120px] mix-blend-screen pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[30rem] h-[30rem] bg-cyan-500/10 rounded-full blur-[100px] mix-blend-screen pointer-events-none" />
        
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]" 
          style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '4rem 4rem' }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10 flex flex-col justify-center h-full">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-5xl"
        >
          {/* Top subtle element */}
          <motion.div variants={textVariants} className="flex items-center gap-6 mb-8">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-black/20 dark:border-white/20 shadow-[0_0_30px_rgba(6,182,212,0.1)] dark:shadow-[0_0_30px_rgba(6,182,212,0.3)] bg-white dark:bg-transparent">
              <img src={ProfileImg} alt="Md. Naimur Rahman" className="w-full h-full object-cover" />
            </div>
            <div className="flex flex-col">
              <span className="text-zinc-900 dark:text-zinc-300 text-lg md:text-xl font-medium tracking-wide">Md. Naimur Rahman</span>
              <span className="text-zinc-600 dark:text-zinc-500 text-sm flex items-center gap-2 mt-1">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Available for work
              </span>
            </div>
          </motion.div>

          {/* Massive Typography - Scaled down for better fit */}
          <motion.div variants={textVariants} className="overflow-hidden mb-2 flex flex-col">
            <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold heading-font leading-[1] tracking-tighter">
              <span className="block text-zinc-900 dark:text-white mb-2">SOFTWARE</span>
              <div className="flex flex-wrap items-center gap-4 md:gap-8">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-indigo-600 dark:from-cyan-400 dark:to-indigo-500">
                  ENGINEER
                </span>
                <span className="hidden md:inline-block w-16 h-1.5 md:w-32 md:h-2 bg-black/20 dark:bg-white/20 rounded-full mt-2 md:mt-6"></span>
              </div>
            </h1>
          </motion.div>

          {/* Subtext */}
          <motion.p variants={textVariants} className="text-lg md:text-xl text-zinc-600 dark:text-zinc-400 max-w-2xl font-light leading-relaxed mb-8 md:mb-12">
            Resolving complex design problems, building smart user interfaces, and engineering seamless web experiences.
          </motion.p>

          {/* Minimalist Actions */}
          <motion.div variants={textVariants} className="flex flex-wrap items-center gap-8">
            <Link
              to="#projects"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("projects")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group flex items-center gap-3 text-lg font-medium text-zinc-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 flex items-center justify-center group-hover:bg-cyan-500/10 group-hover:border-cyan-500/30 transition-all">
                <ArrowDown className="w-5 h-5" />
              </div>
              Explore Work
            </Link>

            <div className="h-8 w-px bg-black/10 dark:bg-white/10 hidden md:block"></div>

            <div className="flex gap-5">
              <a href="https://github.com/Md-Nrahman" target="_blank" rel="noreferrer" aria-label="GitHub Profile" className="text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors">
                <Github className="w-6 h-6" />
              </a>
              <a href="http://linkedin.com/in/mdnrahman/" target="_blank" rel="noreferrer" aria-label="LinkedIn Profile" className="text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors">
                <Linkedin className="w-6 h-6" />
              </a>
              <a href="mailto:md.naimur.rahman.naim@gmail.com" aria-label="Email Me" className="text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors">
                <Mail className="w-6 h-6" />
              </a>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-zinc-50 dark:from-black to-transparent pointer-events-none" />
    </section>
  );
};

export default HeroSection;
